import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import type { TafProfileInfo } from '../../../../types/network';
import { getTafReadings, type TafReadingsResponse, type TafRegisterSeries } from '../../../../services/meterReadingsService';
import { useZoneStore } from '../../../../store/zoneStore';
import { berlinDayEndUtcIso, formatBerlin, parseUtcTimestampMs } from '../../../../utils/timeUtils';
import { fmt } from '../../../../utils/numberFormat';
import { isLoadProfileTaf } from '../../../../utils/smgwUtils';

const TafLoadCurveChart = lazy(() => import('./TafLoadCurveChart'));

/** Window choices: TAF 7 in days of 15-min values, billing profiles in months. */
const LOAD_PROFILE_RANGES = [
  { days: 2, label: '2 Tage' },
  { days: 7, label: '7 Tage' },
  { days: 30, label: '30 Tage' },
  { days: 90, label: '90 Tage' },
] as const;
const BILLING_RANGES = [
  { days: 400, label: '13 Monate' },
  { days: 1100, label: '3 Jahre' },
] as const;

const kwh = (wh: number) => fmt(wh / 1000, Math.abs(wh) >= 100_000 ? 0 : 1);

/** Berlin calendar display — Stichtage and day bounds are cut at a Berlin midnight by the backend. */
const formatDate = (iso: string, withTime: boolean) => withTime
  ? formatBerlin(iso, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : formatBerlin(iso, { day: '2-digit', month: '2-digit', year: 'numeric' });

const registerLabel = (r: TafRegisterSeries) => {
  const dir = r.direction === 'einspeisung' ? '↑ Einspeisung' : r.direction === 'bezug' ? '↓ Bezug' : 'Register';
  return r.tariff ? `${dir} · Tarif ${r.tariff}` : dir;
};

/** Highest interval mean power of a register and when it occurred. */
const peakOf = (r: TafRegisterSeries) => {
  let best: { w: number; at: string } | null = null;
  for (const i of r.intervals) {
    if (!best || i.maxPowerW > best.w) best = { w: i.maxPowerW, at: i.start };
  }
  return best;
};

/**
 * Register values of a TAF-1 / TAF-7 profile, loaded from the Messstellenbetreiber when the
 * user opens this view (GridPilot stores them nowhere). Shows how long the MSB takes and
 * explains every non-delivery (not set up, profile not released, MSB unreachable).
 */
export const TafRegisterView = ({ profile, anchorIso, onShowStored }: {
  profile: TafProfileInfo;
  /** Map time-rewind: the window ends here instead of now. */
  anchorIso?: string;
  /** Opens the values GridPilot itself archived for this profile (archive-fed instances). */
  onShowStored?: () => void;
}) => {
  const loadProfile = isLoadProfileTaf(profile.tafType);
  // Rewound map: the window ends with the Berlin day of the chosen instant — scrubbing within that day keeps the
  // request (every MSB call costs seconds of key checking), and TAF 7 arrives per day anyway.
  const windowEnd = anchorIso ? berlinDayEndUtcIso(anchorIso) : undefined;
  const ranges = loadProfile ? LOAD_PROFILE_RANGES : BILLING_RANGES;
  const [days, setDays] = useState<number>(loadProfile ? 7 : 400);
  const [reload, setReload] = useState(0);
  const [result, setResult] = useState<TafReadingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [elapsedTick, setElapsedTick] = useState(0);
  // "Neu laden" asks the MSB again instead of the backend's in-memory copy — only for that one request
  const forceRef = useRef(false);
  const reloadNow = () => { forceRef.current = true; setReload(x => x + 1); };

  useEffect(() => {
    const controller = new AbortController();
    const zoneAtStart = useZoneStore.getState().activeZoneId;
    setLoading(true);
    setError(null);
    setElapsedTick(0);
    const force = forceRef.current;
    forceRef.current = false;
    getTafReadings(profile.tafLogicalName, days, windowEnd, controller.signal, force)
      .then(r => {
        if (controller.signal.aborted || useZoneStore.getState().activeZoneId !== zoneAtStart) return;
        setResult(r);
      })
      .catch(e => {
        if (controller.signal.aborted) return;
        setResult(null);
        setError(e instanceof Error ? e.message : 'Abruf fehlgeschlagen');
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [profile.tafLogicalName, days, windowEnd, reload]);

  // Seconds counter while the MSB answers (tick counter, see frontend CLAUDE.md)
  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setElapsedTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [loading]);

  const ok = result?.status === 'ok';
  const registers = ok ? result.registers : [];
  const hasValues = registers.some(r => r.readings.length > 0 || r.intervals.length > 0);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1">
        {ranges.map(r => (
          <button
            key={r.days}
            onClick={() => setDays(r.days)}
            disabled={loading && days === r.days}
            className={`text-[11px] px-2 py-0.5 rounded transition-colors ${days === r.days
              ? 'bg-teal-600 text-white'
              : 'bg-gray-100 dark:bg-primary-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-primary-600'}`}
          >
            {r.label}
          </button>
        ))}
        {anchorIso && (
          <span className="ml-auto text-[10px] text-orange-600 dark:text-orange-300" title="Historische Ansicht: Das Zeitfenster endet mit dem Tag des gewählten Zeitpunkts.">
            bis Ende {formatDate(anchorIso, false)}
          </span>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-3 text-xs text-gray-500 dark:text-primary-300">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-500" />
          <span>Lade beim Messstellenbetreiber … {elapsedTick} s</span>
        </div>
      )}

      {!loading && error && (
        <div className="text-xs rounded px-2 py-1.5 bg-error-50 dark:bg-error-900/20 text-error-700 dark:text-error-300 flex items-center justify-between gap-2">
          <span>{error}</span>
          <button onClick={reloadNow} className="underline shrink-0">Erneut</button>
        </div>
      )}

      {!loading && result && !ok && (
        <div className={`text-xs rounded px-2 py-1.5 space-y-1 ${result.status === 'upstream_error'
          ? 'bg-error-50 dark:bg-error-900/20 text-error-700 dark:text-error-300'
          : 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200'}`}
        >
          <div>{result.message ?? 'Der Messstellenbetreiber hat keine Werte geliefert.'}</div>
          <div className="flex items-center gap-3">
            {result.durationMs > 0 && <span className="opacity-70">Antwort nach {fmt(result.durationMs / 1000, 1)} s</span>}
            <button onClick={reloadNow} className="underline">Erneut versuchen</button>
            {onShowStored && result.status === 'not_configured' && (
              <button onClick={onShowStored} className="underline">In GridPilot archivierte Werte</button>
            )}
          </div>
        </div>
      )}

      {!loading && ok && !hasValues && (
        <div className="text-xs text-gray-500 dark:text-primary-400">
          Für diesen Zeitraum liefert der Messstellenbetreiber keine Werte
          {result.dataAvailableTo ? ` (Daten liegen bis ${formatDate(result.dataAvailableTo, true)} vor)` : ''}.
        </div>
      )}

      {!loading && ok && hasValues && loadProfile && (
        <>
          <div className="grid grid-cols-2 gap-1.5">
            {registers.filter(r => r.direction && (r.tariff ?? 0) === 0).map(r => {
              const peak = peakOf(r);
              return (
                <div key={r.obisCode} className="bg-gray-50 dark:bg-primary-700 rounded px-2 py-1.5 text-xs">
                  <div className="text-gray-500 dark:text-primary-400">{registerLabel(r)}</div>
                  <div className={`font-mono font-semibold text-sm ${r.direction === 'einspeisung' ? 'text-success-600 dark:text-success-400' : 'text-info-600 dark:text-info-400'}`}>
                    {kwh(r.energyWh)} kWh
                  </div>
                  {peak && (
                    <div className="text-[10px] text-gray-500 dark:text-primary-400" title="Höchste mittlere Leistung einer Viertelstunde im Zeitraum">
                      max. {fmt(peak.w / 1000)} kW · {formatDate(peak.at, true)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <Suspense fallback={<div className="h-36 bg-gray-50 dark:bg-primary-700 rounded" />}>
            <TafLoadCurveChart registers={registers} resolutionMinutes={registers[0]?.resolutionMinutes ?? 15} />
          </Suspense>
        </>
      )}

      {!loading && ok && hasValues && !loadProfile && registers.map(r => {
        const intervalByEnd = new Map(r.intervals.map(i => [i.end, i]));
        const rows = [...r.readings].sort((a, b) => parseUtcTimestampMs(b.timestamp) - parseUtcTimestampMs(a.timestamp));
        return (
          <div key={r.obisCode}>
            <div className="text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-0.5">
              {registerLabel(r)} <span className="font-mono font-normal text-gray-400">{r.obisCode}</span>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-500 dark:text-primary-400">
                  <th className="text-left font-medium py-0.5">Stichtag</th>
                  <th className="text-right font-medium py-0.5">Zählerstand</th>
                  <th className="text-right font-medium py-0.5" title="Differenz zum vorherigen Zählerstand">Verbrauch</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(p => {
                  const delta = intervalByEnd.get(p.timestamp);
                  return (
                    <tr key={p.timestamp} className="border-t border-gray-100 dark:border-primary-600">
                      <td className="py-0.5 text-gray-700 dark:text-gray-300">{formatDate(p.timestamp, false)}</td>
                      <td className="py-0.5 text-right font-mono text-gray-700 dark:text-gray-300">{kwh(p.valueWh)} kWh</td>
                      <td className="py-0.5 text-right font-mono text-gray-500 dark:text-gray-400">{delta ? `${kwh(delta.energyWh)} kWh` : '–'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}

      {!loading && result && result.status === 'ok' && (
        <div className="text-[10px] text-gray-400 dark:text-primary-400 flex flex-wrap gap-x-2">
          <span title="Die Werte werden bei jedem Öffnen beim Messstellenbetreiber abgerufen und in GridPilot nicht gespeichert.">
            vom Messstellenbetreiber{result.fromCache ? ' (zwischengespeichert)' : ''}, Abruf {fmt(result.durationMs / 1000, 1)} s
          </span>
          {result.dataAvailableTo && <span>· Daten bis {formatDate(result.dataAvailableTo, true)}</span>}
          {registers.some(r => r.negativeSteps > 0) && <span title="Ein Zählerstand ging zurück (Zählerwechsel oder Rücksetzung); diese Intervalle fehlen.">· Zählerwechsel im Zeitraum</span>}
          {result.truncated && <span className="text-orange-600 dark:text-orange-300">· gekürzt (Abrufgrenze des MSB)</span>}
          <button onClick={reloadNow} className="underline ml-auto">Neu laden</button>
        </div>
      )}
    </div>
  );
};
